import { z } from 'zod';
import type {
  CreateDecisionCommand,
  DecisionDto,
  PhaseId,
  UpdateDecisionCommand,
} from '@/api/generated';
import { useCreateDecision, useUpdateDecision } from '@/api/hooks';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/design-system/primitives/Dialog/Dialog';
import { Form, FormField, FormSubmitButton } from '@/design-system/primitives/Form/Form';
import { Input } from '@/design-system/primitives/Input/Input';
import styles from './DecisionDialogs.module.css';

const PHASE_OPTIONS: Array<{ id: PhaseId; label: string }> = [
  { id: 'Voor', label: 'Voorbereiding' },
  { id: 'Ontw', label: 'Ontwerp' },
  { id: 'Verg', label: 'Vergunningen' },
  { id: 'Aanb', label: 'Aanbesteding' },
  { id: 'Ruw', label: 'Ruwbouw' },
  { id: 'Wwd', label: 'Wind-/waterdicht' },
  { id: 'Tech', label: 'Technieken' },
  { id: 'Afw', label: 'Afwerking' },
  { id: 'Opl', label: 'Oplevering' },
  { id: 'Naz', label: 'Nazorg' },
];

// Bouwheers think in commas; the wire format expects an array. The schema
// validates the parsed-out array (≥ 2 distinct, non-empty entries) so the
// dialog can refuse "only one option" without ever round-tripping bad data.
const schema = z.object({
  title: z.string().trim().min(2, 'Minstens 2 tekens'),
  optionsCsv: z.string().refine((raw) => parseOptions(raw).length >= 2, {
    message: 'Minstens 2 opties, gescheiden door komma',
  }),
  deadline: z.string().min(1, 'Kies een datum'),
  phaseId: z.string().min(1, 'Kies een fase'),
});

type DecisionFormValues = z.infer<typeof schema>;

function parseOptions(raw: string): string[] {
  const parts = raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  // Deduplicate while preserving order.
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of parts) {
    if (!seen.has(p)) {
      seen.add(p);
      out.push(p);
    }
  }
  return out;
}

function FieldsBody() {
  return (
    <>
      <FormField<DecisionFormValues> name="title" label="Wat moet er beslist worden?" required>
        {(field) => <Input {...field} autoFocus placeholder="Bv. type vloerafwerking woonkamer" />}
      </FormField>
      <FormField<DecisionFormValues>
        name="optionsCsv"
        label="Opties"
        hint="Scheid met komma's, bv. eik, walnoot, beton"
        required
      >
        {(field) => <Input {...field} value={(field.value as string) ?? ''} />}
      </FormField>
      <FormField<DecisionFormValues> name="deadline" label="Deadline" required>
        {(field) => (
          <Input
            {...field}
            value={(field.value as string) ?? ''}
            type="date"
            onChange={(e) => field.onChange(e.target.value)}
          />
        )}
      </FormField>
      <FormField<DecisionFormValues> name="phaseId" label="Fase" required>
        {(field) => (
          <select
            {...field}
            className={styles.select}
            value={(field.value as string) ?? ''}
            onChange={(e) => field.onChange(e.target.value)}
          >
            {PHASE_OPTIONS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        )}
      </FormField>
    </>
  );
}

export function DecisionCreateDialog({
  open,
  onOpenChange,
  defaultPhaseId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultPhaseId?: PhaseId;
}) {
  const createMutation = useCreateDecision();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Nieuwe beslissing</DialogTitle>
        <DialogDescription>
          Leg de keuze, de opties en de deadline vast. Voorkomt later discussies.
        </DialogDescription>
        <DialogBody>
          <Form<DecisionFormValues>
            schema={schema}
            defaultValues={{
              title: '',
              optionsCsv: '',
              deadline: '',
              phaseId: defaultPhaseId ?? 'Ruw',
            }}
            onSubmit={async (values) => {
              const body: CreateDecisionCommand = {
                title: values.title.trim(),
                options: parseOptions(values.optionsCsv),
                deadline: values.deadline,
                phaseId: values.phaseId,
              };
              await createMutation.mutateAsync(body);
              onOpenChange(false);
            }}
          >
            <FieldsBody />
            <DialogFooter>
              <DialogClose asChild>
                <Btn type="button">Annuleer</Btn>
              </DialogClose>
              <FormSubmitButton>Toevoegen</FormSubmitButton>
            </DialogFooter>
          </Form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

export function DecisionEditDialog({
  open,
  onOpenChange,
  decision,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  decision: DecisionDto;
}) {
  const updateMutation = useUpdateDecision(decision.id);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Beslissing bewerken</DialogTitle>
        <DialogDescription>Pas titel, opties, deadline of fase aan.</DialogDescription>
        <DialogBody>
          <Form<DecisionFormValues>
            schema={schema}
            defaultValues={{
              title: decision.title,
              optionsCsv: decision.options.join(', '),
              deadline: (decision.deadline ?? '').slice(0, 10),
              phaseId: decision.phaseId,
            }}
            onSubmit={async (values) => {
              const body: UpdateDecisionCommand = {
                title: values.title.trim() || null,
                options: parseOptions(values.optionsCsv),
                deadline: values.deadline || null,
                phaseId: values.phaseId,
              };
              await updateMutation.mutateAsync(body);
              onOpenChange(false);
            }}
          >
            <FieldsBody />
            <DialogFooter>
              <DialogClose asChild>
                <Btn type="button">Annuleer</Btn>
              </DialogClose>
              <FormSubmitButton>Opslaan</FormSubmitButton>
            </DialogFooter>
          </Form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
